import {
    LayoutDashboard,
    Bell,
    CheckCircle2,
    ShieldAlert,
    ShieldCheck,
    Beaker,
    BookOpen,
    Boxes,
    FileCode2,
    FileUp,
    GitBranch,
    Inbox,
    Library,
    Network,
    ScanSearch,
    Search,
    Send,
    SlidersHorizontal,
    Workflow,
    Rss,
    Building2,
    Bug,
    KeyRound,
    Package,
    ScrollText,
    Settings,
    Sparkles,
    Users,
    Zap,
} from "lucide-react";
import { Link } from "@tanstack/react-router";

import { useCurrentUser } from "~/lib/auth";
import { useSubmissionsStats } from "~/lib/portal-submissions";

interface NavItem {
    to: string;
    label: string;
    icon: React.ReactNode;
    /** Roles allowed to see this entry. Empty / undefined = all authenticated users. */
    roles?: ReadonlyArray<string>;
}

const PRIMARY: ReadonlyArray<NavItem> = [
    { to: "/", label: "Dashboard", icon: <LayoutDashboard size={16} /> },
    { to: "/iocs", label: "IoCs", icon: <ShieldAlert size={16} /> },
    { to: "/cves", label: "CVEs", icon: <Bug size={16} /> },
    { to: "/heuristic", label: "Heuristic", icon: <Sparkles size={16} />, roles: ["admin", "operator", "analyst"] },
];

// Data intake — feeds (continuous) + imports (one-off) + template library
// + customer submissions queue (Phase 2.D).
const DATA_INTAKE: ReadonlyArray<NavItem> = [
    { to: "/feeds",             label: "Feeds",       icon: <Rss size={16} />,        roles: ["admin", "operator"] },
    { to: "/imports",           label: "Imports",     icon: <FileUp size={16} />,     roles: ["admin", "operator"] },
    { to: "/imports/templates", label: "Templates",   icon: <Library size={16} />,    roles: ["admin", "operator"] },
    { to: "/submissions",       label: "Submissions", icon: <Inbox size={16} />,      roles: ["admin", "operator", "analyst"] },
];

// Automation — sits above Detection. Visual workflow builder + review queue.
const AUTOMATION: ReadonlyArray<NavItem> = [
    { to: "/workflows",          label: "Workflows",     icon: <Workflow size={16} />,         roles: ["admin", "operator"] },
    { to: "/workflows/reviews",  label: "Review queue",  icon: <Inbox size={16} />,            roles: ["admin", "operator", "workflow_approver"] },
    { to: "/workflows/settings", label: "Settings",      icon: <SlidersHorizontal size={16} />, roles: ["admin"] },
];

// Detection — sits between the primary nav and the Platform section. Only
// shown to admin or operator (the same roles that can see the rules API).
// Per-type entries are alphabetized so the eye finds them by name; the
// Overview link sits on top, Reviews + Settings sit at the bottom. Each
// per-type icon is unique across the entire rail (no two share a glyph).
const DETECTION: ReadonlyArray<NavItem> = [
    { to: "/rules",          label: "Overview",      icon: <FileCode2 size={16} />,         roles: ["admin", "operator"] },
    { to: "/rules/custom",   label: "Custom",        icon: <Boxes size={16} />,              roles: ["admin", "operator"] },
    { to: "/rules/elastic",  label: "Elastic",       icon: <Search size={16} />,             roles: ["admin", "operator"] },
    { to: "/rules/sigma",    label: "Sigma",         icon: <BookOpen size={16} />,           roles: ["admin", "operator"] },
    { to: "/rules/suricata", label: "Suricata",      icon: <Network size={16} />,            roles: ["admin", "operator"] },
    { to: "/rules/yara",     label: "YARA",          icon: <ScanSearch size={16} />,         roles: ["admin", "operator"] },
    { to: "/rules/registries", label: "Registries",  icon: <GitBranch size={16} />,         roles: ["admin"] },
    { to: "/rules/reviews",  label: "Reviews",       icon: <Inbox size={16} />,              roles: ["admin", "operator", "rule_approver"] },
    { to: "/rules/settings", label: "Settings",      icon: <SlidersHorizontal size={16} />, roles: ["admin"] },
];

const PLATFORM: ReadonlyArray<NavItem> = [
    { to: "/tenants", label: "Tenants", icon: <Building2 size={16} />, roles: ["admin"] },
    { to: "/products", label: "Products", icon: <Package size={16} />, roles: ["admin"] },
    { to: "/licenses", label: "Licenses", icon: <KeyRound size={16} />, roles: ["admin"] },
    { to: "/users", label: "Users", icon: <Users size={16} />, roles: ["admin"] },
    { to: "/settings", label: "Settings", icon: <Settings size={16} /> },
];

const COMPLIANCE: ReadonlyArray<NavItem> = [
    { to: "/approvals", label: "Approvals", icon: <CheckCircle2 size={16} />, roles: ["admin", "rule_approver", "workflow_approver"] },
    { to: "/audit", label: "Audit log", icon: <ScrollText size={16} />, roles: ["admin"] },
];

// Phase 2.G — Docs entry. Visible to all roles (no `roles:` filter); the
// developer documentation is an in-app surface anyone can reach.
const DEVELOPER: ReadonlyArray<NavItem> = [
    { to: "/docs", label: "Docs", icon: <BookOpen size={16} /> },
    { to: "/docs/playground", label: "API playground", icon: <Beaker size={16} /> },
];

// Notifications — Phase 2.F. Three entries: channel registry, event log,
// delivery log. All three are admin/operator only — analysts can read
// them via the audit log if they really need to.
const NOTIFICATIONS: ReadonlyArray<NavItem> = [
    { to: "/notifications",             label: "Channels",   icon: <Bell size={16} />,  roles: ["admin", "operator"] },
    { to: "/notifications/events",      label: "Events",     icon: <Zap size={16} />,   roles: ["admin", "operator"] },
    { to: "/notifications/deliveries",  label: "Deliveries", icon: <Send size={16} />,  roles: ["admin", "operator"] },
];

function visible(item: NavItem, roles: ReadonlyArray<string>): boolean {
    if (!item.roles || item.roles.length === 0) return true;
    return item.roles.some((r) => roles.includes(r));
}

export function LeftRail(): React.ReactElement {
    const { data: user } = useCurrentUser();
    const roles = user?.roles ?? [];
    const primary = PRIMARY.filter((i) => visible(i, roles));
    const dataIntake = DATA_INTAKE.filter((i) => visible(i, roles));
    const automation = AUTOMATION.filter((i) => visible(i, roles));
    const detection = DETECTION.filter((i) => visible(i, roles));
    const platform = PLATFORM.filter((i) => visible(i, roles));
    const compliance = COMPLIANCE.filter((i) => visible(i, roles));
    const notifications = NOTIFICATIONS.filter((i) => visible(i, roles));
    const developer = DEVELOPER.filter((i) => visible(i, roles));

    return (
        <aside className="app-shell__rail" aria-label="Primary navigation">
            <div className="brandmark">
                <span className="brandmark__dot" aria-hidden />
                <span>IntelRoom</span>
            </div>

            <nav className="stack-xs" style={{ marginTop: "var(--space-2)" }}>
                {primary.map((item) => (
                    <Link
                        key={item.to}
                        to={item.to}
                        className="rail-link"
                        activeOptions={{ exact: item.to === "/" }}
                    >
                        {item.icon}
                        <span>{item.label}</span>
                    </Link>
                ))}
            </nav>

            {dataIntake.length > 0 && (
                <>
                    <div className="rail-section cluster-xs" style={{ gap: 6, alignItems: "center" }}>
                        <Rss size={12} aria-hidden />
                        <span>Data intake</span>
                    </div>
                    <nav className="stack-xs">
                        {dataIntake.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className="rail-link"
                                activeOptions={{ exact: item.to === "/imports" || item.to === "/feeds" }}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                                {item.to === "/submissions" && <SubmissionsBadge />}
                            </Link>
                        ))}
                    </nav>
                </>
            )}

            {automation.length > 0 && (
                <>
                    <div className="rail-section cluster-xs" style={{ gap: 6, alignItems: "center" }}>
                        <GitBranch size={12} aria-hidden />
                        <span>Automation</span>
                    </div>
                    <nav className="stack-xs">
                        {automation.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className="rail-link"
                                activeOptions={{ exact: item.to === "/workflows" }}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </Link>
                        ))}
                    </nav>
                </>
            )}

            {detection.length > 0 && (
                <>
                    <div className="rail-section cluster-xs" style={{ gap: 6, alignItems: "center" }}>
                        <ShieldCheck size={12} aria-hidden />
                        <span>Detection</span>
                    </div>
                    <nav className="stack-xs">
                        {detection.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className="rail-link"
                                activeOptions={{ exact: item.to === "/rules" }}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </Link>
                        ))}
                    </nav>
                </>
            )}

            {platform.length > 0 && (
                <>
                    <div className="rail-section">Platform</div>
                    <nav className="stack-xs">
                        {platform.map((item) => (
                            <Link key={item.to} to={item.to} className="rail-link">
                                {item.icon}
                                <span>{item.label}</span>
                            </Link>
                        ))}
                    </nav>
                </>
            )}

            {notifications.length > 0 && (
                <>
                    <div className="rail-section cluster-xs" style={{ gap: 6, alignItems: "center" }}>
                        <Bell size={12} aria-hidden />
                        <span>Notifications</span>
                    </div>
                    <nav className="stack-xs">
                        {notifications.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className="rail-link"
                                activeOptions={{ exact: item.to === "/notifications" }}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </Link>
                        ))}
                    </nav>
                </>
            )}

            {compliance.length > 0 && (
                <>
                    <div className="rail-section cluster-xs" style={{ gap: 6, alignItems: "center" }}>
                        <ScrollText size={12} aria-hidden />
                        <span>Compliance</span>
                    </div>
                    <nav className="stack-xs">
                        {compliance.map((item) => (
                            <Link key={item.to} to={item.to} className="rail-link">
                                {item.icon}
                                <span>{item.label}</span>
                            </Link>
                        ))}
                    </nav>
                </>
            )}

            {developer.length > 0 && (
                <>
                    <div className="rail-section cluster-xs" style={{ gap: 6, alignItems: "center" }}>
                        <BookOpen size={12} aria-hidden />
                        <span>Developer</span>
                    </div>
                    <nav className="stack-xs">
                        {developer.map((item) => (
                            <Link key={item.to} to={item.to} className="rail-link">
                                {item.icon}
                                <span>{item.label}</span>
                            </Link>
                        ))}
                    </nav>
                </>
            )}
        </aside>
    );
}

// SubmissionsBadge fetches the queue stats and renders a small pill with
// the pending count. Hidden when count is zero so the rail stays calm.
function SubmissionsBadge(): React.ReactElement | null {
    const stats = useSubmissionsStats();
    const pending = stats.data?.counts.pending ?? 0;
    if (pending === 0) return null;
    return (
        <span
            className="status-pill status-pill--info"
            style={{ marginLeft: "auto", fontSize: 10, padding: "2px 6px" }}
            aria-label={`${pending} pending`}
        >
            {pending}
        </span>
    );
}
